import { useSearchParams } from "react-router-dom";
import {
  AggregateScoreType,
  FindingType,
  ScoreStateFilter,
  useGetCheckMqueryQuery,
  useGetCheckReportAggregateNodeDataQuery,
} from "~/operations";
import { useFindingRiskFactors } from "~/pages/space/security/components/RiskFactors/hooks/useFindingRiskFactors";
import { ScoreBlastRadius } from "~/pages/space/security/components/Check/ScoreBlock/ScoreBlastRadius";
import { getCheckStats } from "~/pages/space/security/components/Check/utils";
import { useReportingPageLoaded } from "~/pages/reports/hooks/useReportingPageLoaded";
import {
  FindingsEdge,
  useCheckReportsFetchAllFindings,
} from "./useCheckReportsFetchAllFindings";
import { affectedRiskFactorsCount } from "~/lib/affectedRiskFactorsCount";
import { ExposedAsset } from "~/pages/space/vulnerabilities/components/exposed-assets/types";

export function useCheckReport() {
  const [searchParams] = useSearchParams();
  const scopeMrn = searchParams.get("scopeMrn") || "";
  const findingMrn = searchParams.get("findingMrn") || "";

  const {
    data: checkData,
    loading: checkLoading,
    error: checkError,
    networkStatus: mqueryNetworkStatus,
  } = useGetCheckMqueryQuery({
    variables: {
      input: {
        mrn: findingMrn,
      },
    },
    skip: !findingMrn,
  });

  const check = checkData?.mquery;
  const title = check?.title;
  const description = check?.docs?.desc;
  const properties = check?.properties;
  const mql = check?.mql;

  const {
    data: aggData,
    loading: aggLoading,
    error: aggError,
    networkStatus: checkAggregateScoreNetworkStatus,
  } = useGetCheckReportAggregateNodeDataQuery({
    variables: {
      entityMrn: scopeMrn,
      filter: {
        findingMrn: findingMrn,
        scoreType: AggregateScoreType.Check,
      },
    },
    skip: !scopeMrn,
  });

  const {
    allFindings: failedExposedAssets,
    allFindingsNetworkStatus: failedExposedAssetsNetworkStatus,
  } = useCheckReportsFetchAllFindings({
    scopeMrn,
    filter: {
      types: [FindingType.Check],
      mrn: check?.mrn,
      queryTerms: [],
      state: ScoreStateFilter.Open,
    },
    skip: Boolean(!check?.mrn),
  });

  const aggregateNodeData =
    aggData?.aggregateScores?.__typename === "AggregateScoresConnection"
      ? aggData.aggregateScores.edges?.at(0)?.node
      : undefined;

  const riskValue = aggregateNodeData?.riskValue;
  const rating = aggregateNodeData?.rating;
  const cvss = aggregateNodeData?.cvss;

  const remediations =
    check?.docs?.remediations?.__typename === "Remediations"
      ? check?.docs?.remediations
      : { entries: [] };

  const hasRemediations =
    remediations.entries.length > 0 && (check?.variants || []).length === 0;

  const {
    riskFactorsWithDocs,
    riskFactors,
    loading: riskFactorsLoading,
    networkStatuses: riskFactorsNetworkStatuses,
  } = useFindingRiskFactors({
    spaceMrn: scopeMrn,
    findingMrn: findingMrn || "",
    scoreType: AggregateScoreType.Check,
  });

  const networkStatuses = [
    mqueryNetworkStatus,
    checkAggregateScoreNetworkStatus,
    failedExposedAssetsNetworkStatus,
    ...riskFactorsNetworkStatuses,
  ];

  // Determine if all network requests are ready
  useReportingPageLoaded(networkStatuses);

  const stats = getCheckStats({ blastRadius: aggregateNodeData?.blastRadius });

  function mapCheckScoresToAffectedAssets(finding: FindingsEdge[]) {
    return finding?.flatMap(({ node }) => {
      if (!node || node.__typename !== "CheckFinding") return [];

      const asset: ExposedAsset = {
        isAffectedAsset: true,
        id: node?.asset?.id || "",
        mrn: node?.asset?.mrn || "",
        assetMrn: node?.asset?.mrn || "",
        score: node.riskValue || 0,
        lastUpdated: node.lastUpdated,
        riskFactors: node.riskFactors,
        title: node?.asset?.name || "",
        iconId: node?.asset?.icon,
        tags: node?.asset?.tags,
        rating: node.rating,
        exception: node.exception,
        state: node.state,
        spaceRiskDelta: node.spaceRiskDelta,
        __typename: node.__typename,
      };

      return [asset];
    });
  }

  const failedAssets = mapCheckScoresToAffectedAssets(
    failedExposedAssets || [],
  );

  const mappings = [
    { group: "Risk Factors", value: affectedRiskFactorsCount(riskFactors) },
    ...(aggregateNodeData?.blastRadius && aggregateNodeData.rating
      ? [
          {
            group: "Blast Radius",
            chart: (
              <ScoreBlastRadius
                blastRadius={aggregateNodeData?.blastRadius}
                rating={aggregateNodeData?.rating}
              />
            ),
            value: aggregateNodeData.blastRadius.affected || 0,
          },
        ]
      : []),
  ];

  return {
    isLoading: checkLoading || aggLoading || riskFactorsLoading,
    hasError: checkError || aggError,
    title,
    description,
    stats,
    riskValue,
    properties,
    mql,
    rating,
    cvss,
    remediations,
    hasRemediations,
    riskFactorsWithDocs,
    mappings,
    failedAssets,
  };
}
